import axios from "axios";
import i18next from "i18next";
import { extractApiErrorMessage, isJsonErrorBlob } from "@/utils/apiError";
import { backendApi } from "@/utils/URL";

const api = axios.create({
    baseURL: backendApi() || "http://localhost:8000/api",
    headers: {
        Accept: "application/json",
    },
    transformResponse: [
        (data) => {
            if (typeof data === "string") {
                try {
                    return JSON.parse(data);
                } catch {
                    return data;
                }
            }
            return data;
        },
    ],
});

// Request interceptor to attach Bearer token
api.interceptors.request.use((config) => {
    config.headers["Accept-Language"] = i18next.language || "es";

    const token = localStorage.getItem("token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    const activeCompanyId = localStorage.getItem("vigepra_active_company");
    if (activeCompanyId && activeCompanyId !== "main") {
        config.headers["X-Company-Id"] = activeCompanyId;
    }

    return config;
});

// Response interceptor to handle errors cleanly
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (
            error.response?.status === 401 &&
            !error.config?.url?.includes("/login")
        ) {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            localStorage.removeItem("roles");
            if (
                typeof window !== "undefined" &&
                !window.location.pathname.startsWith("/login")
            ) {
                window.location.href = "/login";
            }
        }

        const message = await extractApiErrorMessage(error);
        return Promise.reject(new Error(message, { cause: error }));
    }
);

export const authApi = {
    login: async (credentials) => {
        const deviceToken = localStorage.getItem("device_token");
        const headers = deviceToken ? { "X-Device-Token": deviceToken } : {};
        const response = await api.post("/login", credentials, { headers });
        return response.data;
    },
    twoFactorChallenge: async (data, tempToken) => {
        const response = await api.post("/login/2fa-challenge", data, {
            headers: {
                Authorization: `Bearer ${tempToken}`,
            },
        });
        return response.data;
    },
    me: async () => {
        const response = await api.get("/me");
        return response.data;
    },
    uploadAvatar: async (file) => {
        const formData = new FormData();
        formData.append("avatar", file, "avatar.webp");
        const response = await api.post("/user/avatar", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return response.data;
    },
    updateConfig: async (config) => {
        const response = await api.patch("/user/config", { config });
        return response.data;
    },
    updatePassword: async (data) => {
        const response = await api.put("/user/security/password", data);
        return response.data;
    },
    enable2fa: async () => {
        const response = await api.post("/user/security/2fa/enable");
        return response.data;
    },
    confirm2fa: async (data) => {
        const response = await api.post("/user/security/2fa/confirm", data);
        return response.data;
    },
    disable2fa: async () => {
        const response = await api.delete("/user/security/2fa");
        return response.data;
    },
    logout: async () => {
        const response = await api.post("/logout");
        return response.data;
    },
    users: async () => {
        const response = await api.get("/users");
        return response.data;
    },
    forgotPassword: (email) => api.post("/forgot-password", { email }),
    webauthnLoginOptions: async (data) => {
        const response = await api.post("/webauthn/login/options", data, {
            withCredentials: true,
        });
        return response.data;
    },
    webauthnLogin: async (data) => {
        const response = await api.post("/webauthn/login", data, {
            withCredentials: true,
        });
        return response.data;
    },
    webauthnRegisterOptions: async () => {
        const response = await api.post(
            "/user/security/webauthn/register/options",
            {},
            { withCredentials: true }
        );
        return response.data;
    },
    webauthnRegister: async (data) => {
        const response = await api.post(
            "/user/security/webauthn/register",
            data,
            { withCredentials: true }
        );
        return response.data;
    },

    webauthnCredentials: async () => {
        const response = await api.get(
            `/user/security/webauthn/credentials?_t=${Date.now()}`,
            { withCredentials: true }
        );
        return response.data?.data || [];
    },
    deleteWebauthnCredential: async (id) => {
        const response = await api.delete(
            `/user/security/webauthn/credentials/${id}`,
            { withCredentials: true }
        );
        return response.data;
    },
};

export const developmentApi = {
    categories: async () => {
        const response = await api.get("/development-categories");
        return response.data;
    },
    suggestTasks: async (id) => {
        const response = await api.post(
            `/developments/${id}/suggest-tasks`,
            {},
            { timeout: 120000 }
        );
        return response.data;
    },
    list: async (params = {}) => {
        const response = await api.get("/developments", { params });
        return response.data;
    },
    get: async (id) => {
        const response = await api.get(`/developments/${id}`);
        return response.data;
    },
    create: async (data) => {
        const response = await api.post("/developments", data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/developments/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/developments/${id}`);
        return response.data;
    },
    reset: async (id) => {
        const response = await api.post(`/developments/${id}/reset`);
        return response.data;
    },
    signStep: async (id, stepKey, actionNote = null, isCompleted = true) => {
        const response = await api.post(
            `/developments/${id}/steps/${stepKey}/action`,
            {
                action_note: actionNote,
                is_completed: isCompleted,
            }
        );
        return response.data;
    },
    sendMessage: async (id, payload) => {
        const response = await api.post(
            `/developments/${id}/messages`,
            payload
        );
        return response.data;
    },
    generateRequirements: async (id) => {
        const response = await api.post(
            `/developments/${id}/requirements-documents`,
            {},
            { timeout: 600000 }
        );
        return response.data;
    },
    getAiConfig: async (id) => {
        const url = id
            ? `/developments/${id}/ai-config`
            : "/developments/ai-config";
        const response = await api.get(url);
        return response.data;
    },
    askAi: async (id, payload) => {
        const response = await api.post(`/developments/${id}/ask-ai`, payload);
        return response.data;
    },
    uploadAttachment: async (
        id,
        file,
        messageId = null,
        isInternal = false,
        category = null,
        requirementAttachmentId = null
    ) => {
        const formData = new FormData();
        formData.append("file", file);
        if (messageId) formData.append("message_id", String(messageId));
        formData.append("is_internal", isInternal ? "1" : "0");
        if (category) formData.append("category", category);
        if (requirementAttachmentId)
            formData.append(
                "requirement_attachment_id",
                String(requirementAttachmentId)
            );
        const response = await api.post(
            `/developments/${id}/attachments`,
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            }
        );
        return response.data;
    },
    downloadAttachmentUrl: (id, attachmentId) => {
        const base = backendApi() || "http://localhost:8000/api";
        return `${base}/developments/${id}/attachments/${attachmentId}`;
    },
    getAttachmentDownloadUrl: (id, attachmentId) => {
        const base = backendApi() || "http://localhost:8000/api";
        return `${base}/developments/${id}/attachments/${attachmentId}`;
    },
    getAttachmentBlob: async (id, attachmentId) => {
        const response = await api.get(
            `/developments/${id}/attachments/${attachmentId}`,
            {
                responseType: "blob",
                params: { _t: Date.now() },
            }
        );
        const jsonError = await isJsonErrorBlob(response.data);
        if (jsonError) {
            throw new Error(jsonError.message);
        }
        return response.data;
    },
    deleteAttachment: async (id, attachmentId) => {
        const response = await api.delete(
            `/developments/${id}/attachments/${attachmentId}`
        );
        return response.data;
    },
    renameAttachment: async (id, attachmentId, name) => {
        const response = await api.patch(
            `/developments/${id}/attachments/${attachmentId}`,
            {
                name,
            }
        );
        return response.data;
    },
    updateAttachment: async (id, attachmentId, data) => {
        const response = await api.patch(
            `/developments/${id}/attachments/${attachmentId}`,
            data
        );
        return response.data;
    },
    getParticipants: async (id) => {
        const response = await api.get(`/developments/${id}/participants`);
        return response.data;
    },
    addParticipant: async (id, userId, role = "viewer") => {
        const response = await api.post(`/developments/${id}/participants`, {
            user_id: userId,
            role,
        });
        return response.data;
    },
    removeParticipant: async (id, userId) => {
        const response = await api.delete(
            `/developments/${id}/participants/${userId}`
        );
        return response.data;
    },
    generateJiraTasks: async (id) => {
        const response = await api.post(
            `/developments/${id}/generate-jira-tasks`
        );
        return response.data;
    },
    generateDesign: async (id) => {
        const response = await api.post(
            `/developments/${id}/generate-design`,
            {},
            { timeout: 600000 }
        );
        return response.data;
    },
};

export const tasksApi = {
    list: async (params = {}) => {
        const response = await api.get("/tasks", { params });
        return response.data;
    },
    get: async (id) => {
        const response = await api.get(`/tasks/${id}`);
        return response.data;
    },
    create: async (data) => {
        const response = await api.post("/tasks", data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/tasks/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/tasks/${id}`);
        return response.data;
    },
    reorder: async (data) => {
        const response = await api.post("/tasks/reorder", data);
        return response.data;
    },
    getMessages: async (id) => {
        const response = await api.get(`/tasks/${id}/messages`);
        return response.data;
    },
    sendMessage: async (id, payload) => {
        const response = await api.post(`/tasks/${id}/messages`, payload);
        return response.data;
    },
    updateMessage: async (id, messageId, payload) => {
        const response = await api.put(
            `/tasks/${id}/messages/${messageId}`,
            payload
        );
        return response.data;
    },
    deleteMessage: async (id, messageId) => {
        const response = await api.delete(`/tasks/${id}/messages/${messageId}`);
        return response.data;
    },
    uploadAttachment: async (id, file, messageId = null) => {
        const formData = new FormData();
        formData.append("file", file);
        if (messageId) formData.append("message_id", String(messageId));
        const response = await api.post(`/tasks/${id}/attachments`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        return response.data;
    },
    downloadAttachmentUrl: (id, attachmentId) => {
        const base = backendApi() || "http://localhost:8000/api";
        return `${base}/tasks/${id}/attachments/${attachmentId}`;
    },
    getAttachmentDownloadUrl: (id, attachmentId) => {
        const base = backendApi() || "http://localhost:8000/api";
        return `${base}/tasks/${id}/attachments/${attachmentId}`;
    },
    getAttachmentBlob: async (id, attachmentId) => {
        const response = await api.get(
            `/tasks/${id}/attachments/${attachmentId}`,
            {
                responseType: "blob",
                params: { _t: Date.now() },
            }
        );
        const jsonError = await isJsonErrorBlob(response.data);
        if (jsonError) {
            throw new Error(jsonError.message);
        }
        return response.data;
    },
};

export const taskStatusesApi = {
    list: async () => {
        const response = await api.get("/task-statuses");
        return response.data;
    },
    create: async (data) => {
        const response = await api.post("/task-statuses", data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/task-statuses/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/task-statuses/${id}`);
        return response.data;
    },
    reorder: async (data) => {
        const response = await api.post("/task-statuses/reorder", data);
        return response.data;
    },
};

export const sprintsApi = {
    list: async () => {
        const response = await api.get("/sprints");
        return response.data;
    },
    active: async () => {
        const response = await api.get("/sprints/active");
        return response.data;
    },
    get: async (id) => {
        const response = await api.get(`/sprints/${id}`);
        return response.data;
    },
    create: async (data) => {
        const response = await api.post("/sprints", data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/sprints/${id}`, data);
        return response.data;
    },
    start: async (id, data) => {
        const response = await api.post(`/sprints/${id}/start`, data);
        return response.data;
    },
    complete: async (id, data = {}) => {
        const response = await api.post(`/sprints/${id}/complete`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/sprints/${id}`);
        return response.data;
    },
    moveTasks: async (taskIds, sprintId) => {
        const response = await api.post("/sprints/move-tasks", {
            task_ids: taskIds,
            sprint_id: sprintId,
        });
        return response.data;
    },
    reorder: async (sprints) => {
        const response = await api.post("/sprints/reorder", { sprints });
        return response.data;
    },
};

export const aiUsageApi = {
    list: async (params = {}) => {
        const response = await api.get("/ai-usage", { params });
        return response.data;
    },
    stats: async (params = {}) => {
        const response = await api.get("/ai-usage/stats", { params });
        return response.data;
    },
};

export const passwordApi = {
    getAll: async () => {
        const response = await api.get("/passwords");
        return response.data;
    },

    create: async (data) => {
        const response = await api.post("/passwords", data);
        return response.data;
    },

    update: async (id, data) => {
        const response = await api.put(`/passwords/${id}`, data);
        return response.data;
    },

    delete: async (id) => {
        await api.delete(`/passwords/${id}`);
    },

    reveal: async (id) => {
        const response = await api.get(`/passwords/${id}/reveal`);
        return response.data.password;
    },
};

export const passwordGroupApi = {
    getAll: async () => {
        const response = await api.get("/password-groups");
        return response.data;
    },
    create: async (data) => {
        const response = await api.post("/password-groups", data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/password-groups/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        await api.delete(`/password-groups/${id}`);
    },
};

export const passwordUserGroupApi = {
    getAll: async () => {
        const response = await api.get("/password-user-groups");
        return response.data;
    },
    get: async (id) => {
        const response = await api.get(`/password-user-groups/${id}`);
        return response.data;
    },
    create: async (data) => {
        const response = await api.post("/password-user-groups", data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/password-user-groups/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        await api.delete(`/password-user-groups/${id}`);
    },
};

export const passwordGroupShareApi = {
    getShares: async (groupId) => {
        const response = await api.get(`/password-groups/${groupId}/shares`);
        return response.data;
    },
    updateShares: async (groupId, data) => {
        const response = await api.put(
            `/password-groups/${groupId}/shares`,
            data
        );
        return response.data;
    },
};

export const userManagementApi = {
    getAll: async () => {
        const response = await api.get("/admin-users");
        return response.data;
    },
    create: async (data) => {
        const response = await api.post("/admin-users", data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/admin-users/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/admin-users/${id}`);
        return response.data;
    },
};

export const roleManagementApi = {
    getAll: async () => {
        const response = await api.get("/roles");
        return response.data;
    },
    create: async (data) => {
        const response = await api.post("/roles", data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/roles/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        await api.delete(`/roles/${id}`);
    },
};

export const tenantApi = {
    getAll: async () => {
        const response = await api.get("/companies");
        return response.data;
    },
    create: async (data) => {
        const response = await api.post("/companies", data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/companies/${id}`, data);
        return response.data;
    },
    uploadLogo: async (id, file) => {
        const formData = new FormData();
        formData.append("logo", file, "logo.webp");
        const response = await api.post(`/companies/${id}/logo`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return response.data;
    },
    delete: async (id) => {
        await api.delete(`/companies/${id}`);
    },
    getUsers: async (id) => {
        const response = await api.get(`/companies/${id}/users`);
        return response.data;
    },
    assignUser: async (id, data) => {
        const response = await api.post(`/companies/${id}/users`, data);
        return response.data;
    },
    removeUser: async (id, userId) => {
        await api.delete(`/companies/${id}/users/${userId}`);
    },
    getMenus: async (id) => {
        const response = await api.get(`/companies/${id}/menus`);
        return response.data;
    },
    updateMenus: async (id, data) => {
        const response = await api.put(`/companies/${id}/menus`, data);
        return response.data;
    },
};

export const aiToolsApi = {
    getSyncStatus: async () => {
        const response = await api.get("/ai/sync-knowledge");
        return response.data;
    },
    syncKnowledge: async () => {
        const response = await api.post("/ai/sync-knowledge");
        return response.data;
    },
    fetchUserStories: async (data = {}) => {
        const response = await api.post("/ai/user-stories", data);
        return response.data;
    },
    getCommands: async () => {
        const response = await api.get("/ai/commands");
        return response.data;
    },
};

export const clientApi = {
    list: async (params = {}) => {
        const response = await api.get("/clients", { params });
        return response.data;
    },
    get: async (id) => {
        const response = await api.get(`/clients/${id}`);
        return response.data;
    },
    create: async (data) => {
        const response = await api.post("/clients", data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/clients/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/clients/${id}`);
        return response.data;
    },
    quickCreate: async (data) => {
        const response = await api.post("/clients/quick-create", data);
        return response.data;
    },
    types: async () => {
        const response = await api.get("/client-types");
        return response.data;
    },
    countries: async () => {
        const response = await api.get("/countries");
        return response.data;
    },
    provinces: async () => {
        const response = await api.get("/provinces");
        return response.data;
    },
};

export const tenderApi = {
    list: async (params = {}) => {
        const response = await api.get("/tenders", { params });
        return response.data;
    },
    kpis: async () => {
        const response = await api.get("/tenders/kpis");
        return response.data;
    },
    get: async (id) => {
        const response = await api.get(`/tenders/${id}`);
        return response.data;
    },
    logs: async (id) => {
        const response = await api.get(`/tenders/${id}/logs`);
        return response.data;
    },
    create: async (data) => {
        const response = await api.post("/tenders", data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/tenders/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/tenders/${id}`);
        return response.data;
    },
    /** @param {number} id @param {number|string} status @param {string|null} [reason] */
    changeStatus: async (id, status, reason = null) => {
        const response = await api.post(`/tenders/${id}/status`, {
            status,
            reason,
        });
        return response.data;
    },
    uploadBc3: async (id, file, percentage = 0, passToStudies = false) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("percentage", percentage);
        if (passToStudies) {
            formData.append("pass_to_studies", "1");
        }
        const response = await api.post(`/tenders/${id}/bc3`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return response.data;
    },
    downloadBc3: async (id) => {
        const response = await api.get(`/tenders/${id}/bc3/download`, {
            responseType: "blob",
        });
        return response.data;
    },
    testMigration: async () => {
        const response = await api.get("/tenders-migration/test");
        return response.data;
    },
    runMigration: async (includeHistorical = true) => {
        const response = await api.post("/tenders-migration/run", {
            include_historical: includeHistorical,
        });
        return response.data;
    },
    clientTypes: async () => {
        const response = await api.get("/client-types");
        return response.data;
    },
    workTypes: async (clientTypeId = null) => {
        const params = clientTypeId ? { client_type_id: clientTypeId } : {};
        const response = await api.get("/project-types", { params });
        return response.data;
    },
    projectTypes: async (clientTypeId = null) => {
        const params = clientTypeId ? { client_type_id: clientTypeId } : {};
        const response = await api.get("/project-types", { params });
        return response.data;
    },
    classificationGroups: async () => {
        const response = await api.get("/classification-groups");
        return response.data;
    },
    classificationCategories: async () => {
        const response = await api.get("/classification-categories");
        return response.data;
    },
    statuses: async () => {
        const response = await api.get("/tender-statuses");
        return response.data;
    },
    unitDeadlines: async () => {
        const response = await api.get("/unit-deadlines");
        return response.data;
    },
    paymentMethods: async () => {
        const response = await api.get("/payment-methods");
        return response.data;
    },
};

export const studyApi = {
    list: async (params = {}) => {
        const response = await api.get("/studies", { params });
        return response.data;
    },
    kpis: async () => {
        const response = await api.get("/studies/kpis");
        return response.data;
    },
    get: async (id) => {
        const response = await api.get(`/studies/${id}`);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/studies/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/studies/${id}`);
        return response.data;
    },
    /** @param {number} id @param {number|string} status @param {string|null} [reason] */
    changeStatus: async (id, status, reason = null) => {
        const response = await api.post(`/studies/${id}/status`, {
            status,
            reason,
        });
        return response.data;
    },
    uploadBc3: async (id, file, percentage = 0) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("percentage", percentage);
        const response = await api.post(`/studies/${id}/bc3`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return response.data;
    },
    downloadBc3: async (id) => {
        const response = await api.get(`/studies/${id}/bc3/download`, {
            responseType: "blob",
        });
        return response.data;
    },
    testMigration: async () => {
        const response = await api.get("/studies-migration/test");
        return response.data;
    },
    runMigration: async () => {
        const response = await api.post("/studies-migration/run");
        return response.data;
    },
};

export const userMigrationApi = {
    testRoles: async () => {
        const response = await api.get("/roles-migration/test");
        return response.data;
    },
    runRoles: async () => {
        const response = await api.post("/roles-migration/run");
        return response.data;
    },
    testUsers: async () => {
        const response = await api.get("/users-migration/test");
        return response.data;
    },
    runUsers: async () => {
        const response = await api.post("/users-migration/run");
        return response.data;
    },
};

// Shared authenticated client for typed feature modules.
export { api };
